/**
 * Genius deeplink Back guard (web).
 *
 * Direct Genius opens leave the partner site (or a prior SPA entry) behind in
 * history. React Router `navigate(..., { replace: true })` also overwrites our
 * seal. So we:
 *  1. Arm a capture-phase popstate listener as early as possible (main.tsx)
 *  2. Keep an extra history entry on top of the current Genius URL
 *  3. Re-seal after every SPA navigation
 *  4. On Back (popstate / router POP): show logout confirmation (not about:blank)
 *
 * Disarmed by completeGeniusDeeplinkFlow() after report OTP.
 */
import { requestGeniusLogoutConfirm } from "./geniusLogoutConfirm";
import { registerGeniusDeeplinkDismissListener } from "./geniusDeeplinkDismiss";
import { shouldEnforceGeniusBackTrap } from "./geniusDeeplinkSession";
import { isPathAllowedDuringGeniusNavLock } from "./geniusDeeplinkNavLock";
import { useAuthStore } from "../../../store/useAuthStore";
import { router } from "../../../routes/router";

const TRAP_STATE_KEY = "__geniusBackTrap";
const TRAP_STATE = { [TRAP_STATE_KEY]: 1 as const };
const GENIUS_LOCKED_LOCATION_KEY = "genius_deeplink_locked_location";

let listenerAttached = false;
let trapArmed = false;
let resealTimer: number | null = null;

type LockedGeniusLocation = {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
};

/** Last allowed Genius screen — restored when Back would leave OTP-pending flow. */
let lockedLocation: LockedGeniusLocation | null = null;

function stripTrapFromHistoryState(state: unknown): unknown {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return state;
  }
  if (!(TRAP_STATE_KEY in (state as Record<string, unknown>))) {
    return state;
  }
  const next = { ...(state as Record<string, unknown>) };
  delete next[TRAP_STATE_KEY];
  return Object.keys(next).length > 0 ? next : null;
}

function historyStateWithTrap(base: unknown): Record<string, unknown> {
  const stripped = stripTrapFromHistoryState(base);
  if (stripped && typeof stripped === "object" && !Array.isArray(stripped)) {
    return { ...(stripped as Record<string, unknown>), [TRAP_STATE_KEY]: 1 };
  }
  return { ...TRAP_STATE };
}

function getRouterLocationState(historyState: unknown): unknown {
  if (
    historyState &&
    typeof historyState === "object" &&
    "usr" in (historyState as object)
  ) {
    return (historyState as { usr: unknown }).usr;
  }
  return undefined;
}

function persistLockedLocation(location: LockedGeniusLocation): void {
  try {
    sessionStorage.setItem(
      GENIUS_LOCKED_LOCATION_KEY,
      JSON.stringify(location),
    );
  } catch {
    // ignore
  }
}

function readPersistedLockedLocation(): LockedGeniusLocation | null {
  if (lockedLocation) return lockedLocation;
  try {
    const raw = sessionStorage.getItem(GENIUS_LOCKED_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LockedGeniusLocation;
    if (!parsed?.pathname) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPersistedGeniusLockedLocation(): void {
  lockedLocation = null;
  try {
    sessionStorage.removeItem(GENIUS_LOCKED_LOCATION_KEY);
  } catch {
    // ignore
  }
}

function captureLockedLocation(): void {
  if (typeof window === "undefined") return;
  if (!isGeniusSessionPending()) return;

  const routerLocation = router.state?.location;
  const pathname = routerLocation?.pathname ?? window.location.pathname;
  if (!isPathAllowedDuringGeniusNavLock(pathname)) return;

  lockedLocation = {
    pathname,
    search: routerLocation?.search ?? window.location.search,
    hash: routerLocation?.hash ?? window.location.hash,
    state:
      routerLocation?.state ??
      getRouterLocationState(stripTrapFromHistoryState(window.history.state)),
  };
  persistLockedLocation(lockedLocation);
}

/**
 * Undo browser Back synchronously — must run inside popstate before React Router
 * applies the POP to a prior in-app route.
 */
function trapBackNavigationSync(): void {
  if (typeof window === "undefined") return;

  captureLockedLocation();

  const locked = readPersistedLockedLocation();
  const url = locked
    ? `${locked.pathname}${locked.search}${locked.hash}`
    : currentUrl();
  const state =
    locked?.state ?? stripTrapFromHistoryState(window.history.state);

  try {
    window.history.pushState(historyStateWithTrap(state), "", url);
  } catch {
    // ignore
  }
}

function syncRouterToLockedLocation(): void {
  const locked = readPersistedLockedLocation();
  if (!locked) return;

  void router.navigate(
    { pathname: locked.pathname, search: locked.search, hash: locked.hash },
    {
      replace: true,
      state:
        locked.state != null && typeof locked.state === "object"
          ? locked.state
          : getRouterLocationState(locked.state),
    },
  );
}

function restoreLockedGeniusLocation(): void {
  if (typeof window === "undefined") return;
  trapBackNavigationSync();
  syncRouterToLockedLocation();
}

function handleGeniusBackNavigation(): void {
  if (!isGeniusSessionPending()) return;
  trapBackNavigationSync();
  syncRouterToLockedLocation();
  requestGeniusLogoutConfirm();
}

/** Back / address-bar edit / disallowed route → restore screen + logout popup. */
export function promptGeniusLogoutConfirmForNavViolation(): void {
  if (typeof window === "undefined") return;

  const locked = readPersistedLockedLocation();
  if (locked) {
    const current = currentUrl();
    const target = `${locked.pathname}${locked.search}${locked.hash}`;
    if (current !== target) {
      restoreLockedGeniusLocation();
    }
  }

  requestGeniusLogoutConfirm();
}

function isGeniusSessionPending(): boolean {
  try {
    return shouldEnforceGeniusBackTrap(useAuthStore.getState().profilesData);
  } catch {
    return false;
  }
}

function isTrapState(state: unknown): boolean {
  return Boolean(
    state &&
    typeof state === "object" &&
    (state as Record<string, unknown>)[TRAP_STATE_KEY] === 1,
  );
}

function currentUrl(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function restoreGeniusDocumentVisibility(): void {
  if (typeof document === "undefined") return;
  try {
    document.documentElement.style.visibility = "";
    if (document.body) document.body.style.visibility = "";
  } catch {
    // ignore
  }
}

/** User dismissed the logout dialog — re-seal history for the next Back. */
export function cancelGeniusBackTrapExit(): void {
  sealGeniusDeeplinkHistory();
}

/**
 * Ensure the top history entry is our trap for the current URL.
 * Always safe to call — no-ops if already sealed or session ended.
 */
export function sealGeniusDeeplinkHistory(): void {
  if (typeof window === "undefined") return;
  if (!trapArmed) return;
  if (!isGeniusSessionPending()) return;

  captureLockedLocation();

  if (isTrapState(window.history.state)) return;

  try {
    window.history.pushState(
      historyStateWithTrap(window.history.state),
      "",
      currentUrl(),
    );
  } catch {
    // ignore
  }
}

function scheduleReseal(): void {
  if (typeof window === "undefined") return;
  if (resealTimer != null) window.clearTimeout(resealTimer);

  // RR replaceState often runs in the same tick as navigate — reseal after it.
  resealTimer = window.setTimeout(() => {
    resealTimer = null;
    sealGeniusDeeplinkHistory();
  }, 0);

  window.setTimeout(() => {
    sealGeniusDeeplinkHistory();
  }, 50);

  window.setTimeout(() => {
    sealGeniusDeeplinkHistory();
  }, 200);
}

function onPopState(): void {
  if (!trapArmed || !isGeniusSessionPending()) return;
  handleGeniusBackNavigation();
}

function onPageShow(): void {
  restoreGeniusDocumentVisibility();
  if (trapArmed && isGeniusSessionPending()) {
    sealGeniusDeeplinkHistory();
  }
}

function ensureListeners(): void {
  if (listenerAttached) return;
  window.addEventListener("popstate", onPopState, true);
  window.addEventListener("pageshow", onPageShow);
  listenerAttached = true;
}

function detachListeners(): void {
  if (!listenerAttached) return;
  window.removeEventListener("popstate", onPopState, true);
  window.removeEventListener("pageshow", onPageShow);
  listenerAttached = false;
}

function clearTrapMarkerOnCurrentEntry(): void {
  if (typeof window === "undefined") return;
  if (!isTrapState(window.history.state)) return;
  try {
    window.history.replaceState(
      stripTrapFromHistoryState(window.history.state),
      "",
      currentUrl(),
    );
  } catch {
    // ignore
  }
}

/**
 * Arm / disarm Back → logout confirmation while Genius OTP is pending.
 * Call with true from main.tsx on first Genius deeplink load (before React).
 */
export function syncGeniusDeeplinkBackTrap(enabled: boolean): void {
  if (typeof window === "undefined") return;

  restoreGeniusDocumentVisibility();

  const pending = Boolean(enabled) && isGeniusSessionPending();

  if (!pending) {
    trapArmed = false;
    clearPersistedGeniusLockedLocation();
    if (resealTimer != null) {
      window.clearTimeout(resealTimer);
      resealTimer = null;
    }
    detachListeners();
    clearTrapMarkerOnCurrentEntry();
    return;
  }

  trapArmed = true;
  ensureListeners();
  captureLockedLocation();
  // First-entry: push trap now so the very first Back stays in-document.
  sealGeniusDeeplinkHistory();
  scheduleReseal();
}

export function handleGeniusRouterHistoryPop(): void {
  if (!trapArmed || !isGeniusSessionPending()) return;
  handleGeniusBackNavigation();
}

export function resetGeniusDeeplinkBackTrap(): void {
  syncGeniusDeeplinkBackTrap(false);
}

registerGeniusDeeplinkDismissListener(() => {
  resetGeniusDeeplinkBackTrap();
});
